import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import { fetchBookingHistory } from "../../../../../Redux/slice/features/bookingSlice";
import { showCancellationPage } from "../../../../../Redux/slice/features/showCancel";

const bookingTableStyles = `
  .booking-wrapper {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    margin-top: 1rem;
    border: 1px solid #f1f5f9;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
  }
  .table-header-custom {
    padding: 24px;
    background: #ffffff;
    border-bottom: 1px solid #f1f5f9;
  }
  .filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    align-items: flex-end;
  }
  .custom-input {
    width: 100%;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    font-size: 0.875rem;
    transition: all 0.2s;
  }
  .custom-input:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  .search-btn {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    padding: 11px 24px;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
  }
  .search-btn:hover { background: #1d4ed8; transform: translateY(-1px); }
  
  .export-btn {
    padding: 10px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .export-btn:hover { background: #f8fafc; border-color: #cbd5e1; }

  .booking-table-container {
    overflow-x: auto;
    width: 100%;
  }
  .booking-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 1300px;
  }
  .booking-table th {
    background: #f8fafc;
    color: #64748b;
    font-weight: 700;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 16px 24px;
    border-bottom: 2px solid #f1f5f9;
    text-align: left;
  }
  .booking-table td {
    padding: 18px 24px;
    vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    font-size: 0.875rem;
  }
  .booking-table tbody tr { transition: background 0.2s; }
  .booking-table tbody tr:hover { background: #f8fafc; }

  .ref-no {
    font-family: 'JetBrains Mono', monospace;
    background: #f1f5f9;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 0.8rem;
    color: #475569;
    border: 1px solid #e2e8f0;
  }
  .badge-row {
    padding: 6px 12px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.75rem;
    white-space: nowrap;
  }
  .badge-premium { background: #eff6ff; color: #2563eb; }
  .badge-success { background: #d1fae5; color: #065f46; }
  .badge-danger { background: #fee2e2; color: #dc2626; }
  
  .action-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-view { background: #eff6ff; color: #3b82f6; }
  .btn-view:hover { background: #3b82f6; color: white; }
  .btn-cancel { background: #fee2e2; color: #ef4444; }
  .btn-cancel:hover:not(:disabled) { background: #ef4444; color: white; }
  .btn-cancel:disabled { opacity: 0.4; cursor: not-allowed; }

  .pagination-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    background: #f8fafc;
    border-top: 1px solid #f1f5f9;
  }
  .page-nav-btn {
    padding: 8px 20px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: white;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  .page-nav-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #cbd5e1; }
  .page-nav-btn:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TodayBookingTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const todayDateStr = new Date().toISOString().split("T")[0];

  const user = useSelector((state) => state.auth.user);
  const { data, status } = useSelector((state) => state.booking);
  const userID = user?.user.customerId;
  const isAdmin = user?.user?.UserName === "admin";
  const userHotelId = user?.user?.PropertyID;

  const [filters, setFilters] = useState({
    bookingRefNo: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    if (userID) {
      const fetchParams = { pageSize: 500 };
      fetchParams.fromDate = todayDateStr;
      fetchParams.toDate = todayDateStr;
      
      if (isAdmin) {
        dispatch(fetchBookingHistory(fetchParams));
      } else {
        fetchParams.UserId = userID;
        if (userHotelId) fetchParams.HotelID = userHotelId;
        dispatch(fetchBookingHistory(fetchParams));
      }
    }
  }, [userID, userHotelId, dispatch, isAdmin, todayDateStr]);

  const handleShow = useCallback(() => {
    const params = { ...filters, pageSize: 500, fromDate: todayDateStr, toDate: todayDateStr };
    if (!isAdmin) {
      params.UserId = userID;
      if (userHotelId) params.HotelID = userHotelId;
    }
    dispatch(fetchBookingHistory(params));
    setCurrentPage(1);
  }, [dispatch, userID, userHotelId, filters, isAdmin, todayDateStr]);

  const handleCancellation = useCallback((bookingId) => {
    dispatch(showCancellationPage());
    navigate(`/cancellation/${bookingId}`);
  }, [dispatch, navigate]);

  const handleViewBooking = useCallback((bookingId) => {
    window.open(`/booking-view/${bookingId}`, "_blank", "width=800,height=600");
  }, []);

  const transformedData = useMemo(() => {
    if (status !== "succeeded" || !Array.isArray(data)) return [];
    return data.filter((history) => {
      const isFailed = String(history.Remarks || "").toLowerCase().includes("failed");
      if (isFailed) return false;
      if (isAdmin || !userHotelId) return true;

      const bookingHotelId =
        history.PropertyID ??
        history.HotelID ??
        history.HotelId ??
        history.hotelId ??
        history.intHoteId;

      return !bookingHotelId || Number(bookingHotelId) === Number(userHotelId);
    });
  }, [data, status, isAdmin, userHotelId]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return transformedData.slice(start, start + itemsPerPage);
  }, [transformedData, currentPage]);

  const totalPages = Math.ceil(transformedData.length / itemsPerPage) || 1;

  const handleExcelExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");
    XLSX.writeFile(workbook, "today_bookings.xlsx");
  };

  if (status === "loading") {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <div className="mt-3 text-muted">Loading Bookings...</div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      <style>{bookingTableStyles}</style>
      
      <div className="booking-wrapper">
        <div className="table-header-custom" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '16px' }}>
            <div style={{ minWidth: '250px' }}>
              <label className="form-label small fw-bold text-muted">REFERENCE NO</label>
              <input 
                type="text" 
                className="custom-input" 
                placeholder="Search Reference..." 
                value={filters.bookingRefNo} 
                onChange={(e) => setFilters({...filters, bookingRefNo: e.target.value})}
              />
            </div>
            <button className="search-btn" onClick={handleShow}>
              <i className="bi bi-search"></i> Search
            </button>
          </div>
          <div className="d-flex gap-2">
            <button className="export-btn" onClick={handleExcelExport} title="Export Excel">
              <i className="bi bi-file-earmark-excel"></i>
            </button>
            <CSVLink 
              data={transformedData} 
              filename="today_bookings.csv" 
              className="export-btn"
              title="Export CSV"
            >
              <i className="bi bi-file-earmark-text"></i>
            </CSVLink>
          </div>
        </div>

        <div className="booking-table-container">
          <table className="booking-table">
            <thead>
              <tr>
                <th>Ref No</th>
                <th>Booking Date</th>
                <th>Hotel & Customer</th>
                <th>Check In/Out</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, idx) => {
                  const isCancelled = row.intCancelStatus === 1;
                  return (
                    <tr key={idx}>
                      <td><span className="ref-no">{row.BookingRefNo}</span></td>
                      <td><div className="text-muted small">{row.BookingDate}</div></td>
                      <td>
                        <div className="fw-bold">{row.PropertyName}</div>
                        <div className="small text-muted">{row.CustomerName}</div>
                      </td>
                      <td>
                        <div className="small"><span className="text-muted">In:</span> {row.CheckinDate}</div>
                        <div className="small"><span className="text-muted">Out:</span> {row.CheckOutDate}</div>
                      </td>
                      <td><span className="badge-row badge-premium">{row.Remarks || "-"}</span></td>
                      <td><span className="badge-row badge-success">₹{Number(row.TotalAmount || 0).toLocaleString('en-IN')}</span></td>
                      <td>
                        <div className="d-flex gap-2">
                          <button className="action-btn btn-view" onClick={() => handleViewBooking(row.BookingID)} title="View Receipt">
                            <i className="bi bi-eye-fill"></i>
                          </button>
                          {isAdmin && (
                            <button 
                              className="action-btn btn-cancel" 
                              onClick={() => handleCancellation(row.BookingID)} 
                              disabled={isCancelled}
                              title={isCancelled ? "Already Cancelled" : "Cancel Booking"}
                            >
                              <i className={isCancelled ? "bi bi-x-circle-fill" : "bi bi-x-circle"}></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">No booking records found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-footer">
          <div className="small text-muted">
            Showing <b>{paginatedData.length}</b> of <b>{transformedData.length}</b> total bookings
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="small fw-bold">Page {currentPage} of {totalPages}</div>
            <div className="d-flex gap-2">
              <button 
                className="page-nav-btn" 
                onClick={() => setCurrentPage(p => p - 1)} 
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button 
                className="page-nav-btn" 
                onClick={() => setCurrentPage(p => p + 1)} 
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TodayBookingTable);
